/**
 * Cola serial de escritura IndexedDB (1 put a la vez).
 * Soporta kind dicom | preview.
 * Los fallos de cuota nunca deben afectar la UI de medición (solo log + drop).
 */
import {
  DICOM_OFFLINE_CACHE_ENABLED,
  CACHE_KIND,
  putCachedInstance,
  toBlob,
  ensureSoftQuota,
  isQuotaLikeError,
  recoverFromQuotaPressure,
  onDbFailed,
} from './dicomCache'
import {
  shouldDeferIdbWrites,
  scheduleWhenIdle,
  cancelWhenIdle,
} from '../utils/viewportInteractionDetector'

/** SoftQuota cada N puts (no por instancia). */
const SOFT_QUOTA_EVERY_N = 50

/** Profundidad alta: solo se loguea; no se frena download. */
const WARN_QUEUE_DEPTH = 400

/** Tras N fallos de cuota consecutivos, vaciar cola (no reintentar en loop). */
const MAX_CONSECUTIVE_QUOTA_FAILS = 3

/** Handle requestIdleCallback / setTimeout para retomar tras gesto. */
let idleResumeHandle = null

const state = {
  queue: [],
  running: false,
  putsCompleted: 0,
  putsSinceQuota: 0,
  bytesQueued: 0,
  writesFailed: 0,
  consecutiveQuotaFails: 0,
  pausedWrites: false,
}

// Cuando el IDB queda bloqueado/timeout: limpiar cola y pausar para evitar el
// loop de reintentos Dexie (blocked → close → auto-reopen → blocked → …)
onDbFailed(() => {
  state.queue = []
  state.bytesQueued = 0
  state.pausedWrites = true
  if (idleResumeHandle) {
    try {
      cancelWhenIdle(idleResumeHandle)
    } catch (e) {
      /* ignore */
    }
    idleResumeHandle = null
  }
  console.warn('[dicomCacheWriteQueue] IDB no disponible – cola vaciada, escrituras pausadas.')
})

/**
 * @returns {{
 *   queueDepth: number,
 *   bytesQueued: number,
 *   writesFailed: number,
 *   putsCompleted: number,
 *   pausedWrites: boolean,
 * }}
 */
export function getWriteQueueStats() {
  return {
    queueDepth: state.queue.length + (state.running ? 1 : 0),
    bytesQueued: state.bytesQueued,
    writesFailed: state.writesFailed,
    putsCompleted: state.putsCompleted,
    pausedWrites: !!state.pausedWrites,
  }
}

/**
 * Descarta trabajos pendientes (p.ej. tras QuotaExceeded) para no saturar IDB.
 */
export function clearWriteQueue() {
  state.queue = []
  state.bytesQueued = 0
}

/**
 * Encola un put complete (dicom por defecto).
 * @param {{
 *   sopInstanceUID: string,
 *   studyInstanceUID: string,
 *   blob: Blob|ArrayBuffer,
 *   expectedByteLength?: number,
 *   kind?: 'dicom'|'preview',
 *   contentType?: string,
 * }} job
 */
export function enqueuePutComplete(job) {
  if (!DICOM_OFFLINE_CACHE_ENABLED || !job || !job.sopInstanceUID) return false
  if (state.pausedWrites) return false

  const kind = job.kind === CACHE_KIND.PREVIEW ? CACHE_KIND.PREVIEW : CACHE_KIND.DICOM
  const contentType =
    job.contentType ||
    (kind === CACHE_KIND.PREVIEW ? 'image/jpeg' : 'application/dicom')
  const blob = toBlob(job.blob, contentType)
  if (!blob) return false

  // No encolar si ya hay backlog alto (presión de cuota)
  if (state.queue.length > WARN_QUEUE_DEPTH) {
    return false
  }

  const byteLength = blob.size || 0
  state.queue.push({
    sopInstanceUID: String(job.sopInstanceUID),
    studyInstanceUID: String(job.studyInstanceUID || ''),
    blob,
    expectedByteLength:
      job.expectedByteLength != null ? job.expectedByteLength : byteLength,
    byteLength,
    kind,
    contentType,
  })
  state.bytesQueued += byteLength

  if (state.queue.length >= WARN_QUEUE_DEPTH && state.queue.length % 50 === 0) {
    console.warn(
      '[dicomCacheWriteQueue] backlog depth=',
      state.queue.length,
      'bytesQueued≈',
      Math.round(state.bytesQueued / (1024 * 1024)),
      'MiB'
    )
  }

  pump()
  return true
}

/**
 * @param {{ sopInstanceUID: string, studyInstanceUID?: string, blob: Blob|ArrayBuffer, contentType?: string }} job
 */
export function enqueuePutPreview(job) {
  return enqueuePutComplete({
    ...job,
    kind: CACHE_KIND.PREVIEW,
    contentType: (job && job.contentType) || 'image/jpeg',
  })
}

/**
 * Reactiva escrituras tras recuperación de cuota.
 */
export function resumeWriteQueue() {
  state.pausedWrites = false
  state.consecutiveQuotaFails = 0
  pump()
}

function pump() {
  if (state.running) return
  if (state.pausedWrites) return
  if (state.queue.length === 0) return
  state.running = true
  void runLoop()
}

async function runLoop() {
  while (state.queue.length > 0 && !state.pausedWrites) {
    // Fase A: IDB congelado mid-gesto; retoma en idle al soltar
    try {
      if (shouldDeferIdbWrites()) {
        state.running = false
        cancelWhenIdle(idleResumeHandle)
        idleResumeHandle = scheduleWhenIdle(
          () => {
            idleResumeHandle = null
            try {
              pump()
            } catch (e) {
              /* ignore */
            }
          },
          { timeout: 600 }
        )
        return
      }
    } catch (eIx) {
      /* ignore */
    }
    const job = state.queue.shift()
    state.bytesQueued = Math.max(0, state.bytesQueued - (job.byteLength || 0))
    try {
      state.putsSinceQuota += 1
      if (state.putsSinceQuota >= SOFT_QUOTA_EVERY_N) {
        state.putsSinceQuota = 0
        await ensureSoftQuota()
      }
      const ok = await putCachedInstance({
        sopInstanceUID: job.sopInstanceUID,
        studyInstanceUID: job.studyInstanceUID,
        blob: job.blob,
        status: 'complete',
        expectedByteLength: job.expectedByteLength,
        kind: job.kind || CACHE_KIND.DICOM,
        contentType: job.contentType,
      })
      if (ok) {
        state.putsCompleted += 1
        state.consecutiveQuotaFails = 0
      } else {
        state.writesFailed += 1
        // putCachedInstance absorbe Quota y retorna false
        state.consecutiveQuotaFails += 1
        if (state.consecutiveQuotaFails >= MAX_CONSECUTIVE_QUOTA_FAILS) {
          try {
            await recoverFromQuotaPressure()
          } catch (e2) {
            /* ignore */
          }
          clearWriteQueue()
          state.pausedWrites = true
          console.warn(
            '[dicomCacheWriteQueue] escrituras pausadas (cuota IDB); la medición sigue en RAM'
          )
          break
        }
      }
    } catch (e) {
      state.writesFailed += 1
      if (isQuotaLikeError(e)) {
        state.consecutiveQuotaFails += 1
        console.warn(
          '[dicomCacheWriteQueue] quota put failed (no afecta medición)',
          e && e.message
        )
        try {
          await recoverFromQuotaPressure()
        } catch (e2) {
          /* ignore */
        }
        if (state.consecutiveQuotaFails >= MAX_CONSECUTIVE_QUOTA_FAILS) {
          clearWriteQueue()
          state.pausedWrites = true
          console.warn(
            '[dicomCacheWriteQueue] escrituras pausadas por cuota IDB; se reanudará más tarde'
          )
          break
        }
      } else {
        console.warn('[dicomCacheWriteQueue] put failed', e)
      }
    }
    await new Promise((r) => setTimeout(r, 0))
  }
  state.running = false
  if (state.queue.length > 0 && !state.pausedWrites) {
    try {
      pump()
    } catch (e) {
      /* ignore */
    }
  }
}

/**
 * Espera a que la cola de escritura IDB quede vacía (o timeout).
 * @param {{ timeoutMs?: number, pollMs?: number }} [opts]
 * @returns {Promise<boolean>} true si idle, false si timeout
 */
export function whenWriteQueueIdle(opts = {}) {
  const timeoutMs =
    opts.timeoutMs != null ? Math.max(0, Number(opts.timeoutMs) || 0) : 60000
  const pollMs =
    opts.pollMs != null ? Math.max(20, Number(opts.pollMs) || 80) : 80
  const t0 = Date.now()
  return new Promise((resolve) => {
    const tick = () => {
      const st = getWriteQueueStats()
      if (!st.queueDepth) {
        resolve(true)
        return
      }
      if (Date.now() - t0 >= timeoutMs) {
        resolve(false)
        return
      }
      setTimeout(tick, pollMs)
    }
    tick()
  })
}
